import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, Linkedin } from "lucide-react";
import { useJsonLd, organizationSchema } from "../utils/schema";
import { trackPageView, trackOutboundClick, trackPhoneClick, trackCTAClick } from "../utils/analytics";
import { ensureAttribution } from "../utils/utm";
import "../approved-brand.css";

const NAV_LINKS = [
  { label: "Solutions", path: "/solutions" },
  { label: "Industries", path: "/industries" },
  { label: "PathScore", path: "/path-score" },
  { label: "AreaScore", path: "/area-score" },
  { label: "Talent Network", path: "/talent-network" },
  { label: "Insights", path: "/insights" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useJsonLd("ld-organization", organizationSchema);

  useEffect(() => {
    ensureAttribution();
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e) => {
      const anchor = e.target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href.startsWith("tel:")) trackPhoneClick(href.replace("tel:", ""));
      else if (href.startsWith("mailto:") || href.startsWith("http")) trackOutboundClick(href);
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const isActive = (path) => location.pathname === path || (path !== "/" && location.pathname.startsWith(path));

  return (
    <div className="min-h-screen flex flex-col ols-body-font" style={{ background: "var(--ols-limestone)" }}>
      <a href="#main-content" className="ols-skip-link" data-testid="skip-to-content">Skip to main content</a>
      {/* Top nav */}
      <nav
        className="brand-header fixed top-0 left-0 right-0 z-50"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="brand-header__row flex justify-between items-center gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <img
                src="/brand/onward-approved-logo.png"
                alt="Onward Land Solutions"
                className="brand-logo brand-logo--nav"
                width="1711"
                height="372"
                data-testid="header-approved-logo"
              />
            </Link>

            {/* Desktop nav */}
            <div className="hidden xl:flex items-center gap-4">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`ols-nav-link ${isActive(link.path) ? "ols-nav-link--active" : ""}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact?source=nav_desktop"
                onClick={() => trackCTAClick("nav_start_review")}
                className="ols-btn-primary"
                style={{ padding: "11px 0", fontSize: "13px" }}
                data-testid="nav-cta-start-review"
              >
                Talk to Onward
              </Link>
            </div>

            {/* Mobile / tablet, logo + hamburger only */}
            <div className="xl:hidden flex items-center">
              <button
                className="text-[#191A1C] p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-navigation"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile nav */}
          {mobileMenuOpen && (
            <div id="mobile-navigation" data-testid="mobile-navigation" className="brand-mobile-menu xl:hidden pb-6 pt-2 space-y-3 border-t px-2 -mx-2 max-h-[80vh] overflow-y-auto">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`block text-[15px] font-medium py-2 ${
                    isActive(link.path) ? "brand-mobile-link--active" : "brand-mobile-link"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact?source=nav_mobile"
                onClick={() => { trackCTAClick("nav_mobile_start_review"); setMobileMenuOpen(false); }}
                className="ols-btn-primary w-full justify-center mt-4"
                style={{ display: "flex" }}
                data-testid="mobile-cta-start-review"
              >
                Talk to Onward
              </Link>
            </div>
          )}
        </div>
      </nav>

      <main id="main-content" className="flex-1 pt-[84px]">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="brand-footer pt-16 pb-8" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div className="md:col-span-2">
              <img
                src="/brand/onward-approved-logo.png"
                alt="Onward Land Solutions"
                className="brand-logo brand-logo--footer"
                width="1711"
                height="372"
                data-testid="footer-approved-logo"
              />
              <p className="text-slate-400 text-sm max-w-md leading-relaxed">
                Land project management, right-of-way, corridor intelligence, easement acquisition, GIS-backed
                project tracking, and survey coordination for infrastructure, energy, utility, pipeline,
                transmission, renewable, and development clients.
              </p>
            </div>

            <div>
              <p className="text-white font-semibold text-sm mb-4">Solutions</p>
              <ul className="space-y-2 text-sm">
                <li><Link to="/path-score" data-testid="footer-pathscore" className="text-slate-400 hover:text-white">PathScore</Link></li>
                <li><Link to="/score-a-corridor?source=footer_first_look" data-testid="footer-first-look" className="text-slate-400 hover:text-white">Get a Complimentary PathScore First Look</Link><p className="text-xs text-slate-400 mt-2 leading-relaxed" data-testid="footer-first-look-scope">Complimentary screening; full PathScore is separately scoped paid work.</p></li>
                <li><Link to="/area-score" data-testid="footer-areascore" className="text-slate-400 hover:text-white">AreaScore</Link></li>
                <li><Link to="/solutions" data-testid="footer-solutions" className="text-slate-400 hover:text-white">All Solutions</Link></li>
                <li><Link to="/industries" data-testid="footer-industries" className="text-slate-400 hover:text-white">Industries</Link></li>
              </ul>
            </div>

            <div>
              <p className="text-white font-semibold text-sm mb-4">Company</p>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" data-testid="footer-about" className="text-slate-400 hover:text-white">About</Link></li>
                <li><Link to="/insights" data-testid="footer-insights" className="text-slate-400 hover:text-white">Insights</Link></li>
                <li><Link to="/contact" data-testid="footer-contact" className="text-slate-400 hover:text-white">Contact</Link></li>
                <li><Link to="/talent-network" data-testid="footer-talent-network" className="text-red-400 hover:text-red-300 font-semibold">Talent Network</Link></li>
                <li><Link to="/talent-network/privacy" data-testid="footer-talent-privacy" className="text-slate-400 hover:text-white">Talent Privacy Notice</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" style={{ borderColor: "#E3E3E6" }}>
            <p className="text-slate-500 text-xs">
              © {new Date().getFullYear()} Onward Land Solutions. Fort Worth, Texas. All rights reserved.
              &nbsp;|&nbsp; <a href="tel:+18175775024" data-testid="footer-office-phone" className="text-slate-400 hover:text-white">(817) 577-5024</a>
              &nbsp;|&nbsp; <a href="mailto:info@onwardland.com" data-testid="footer-office-email" className="text-slate-400 hover:text-white">info@onwardland.com</a>
            </p>
            <div className="flex items-center gap-4">
              <a href="https://www.linkedin.com/company/onward-land-solutions" data-testid="footer-linkedin" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white" aria-label="LinkedIn"><Linkedin className="h-4 w-4" /></a>
              <a href="tel:+18175775024" data-testid="footer-phone-icon" className="text-slate-400 hover:text-white" aria-label="Phone"><Phone className="h-4 w-4" /></a>
            </div>
          </div>
          <p className="text-slate-500 text-[11px] mt-6 leading-relaxed">
            Onward Land Solutions does not provide licensed surveying services. Survey work is performed by
            properly licensed survey professionals; Onward coordinates the land, access, communication, and
            project workflow around that work.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
